/**
 * useAssignedShipmentsCount Hook
 * 
 * React hook for counting unconfirmed, non-deleted assigned shipments
 * Used to show badge count in the sidebar menu for sales reps
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { ShipmentService } from '../services/ShipmentService';

interface UseAssignedShipmentsCountReturn {
  count: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const USERS_COLLECTION = 'users';
const ASSIGNED_SHIPMENTS_SUBCOLLECTION = 'assigned_shipments';

export const useAssignedShipmentsCount = (): UseAssignedShipmentsCountReturn => {
  const { user, userRole } = useAuth();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch count of unconfirmed, non-deleted assigned shipments
   */
  const fetchCount = useCallback(async () => {
    if (!user || userRole !== 'sales-representative') {
      setCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Query assigned shipments that are not confirmed
      // Note: Deleted shipments won't exist in the collection, so we only need to check confirmed
      const q = query(
        collection(db, USERS_COLLECTION, user.id, ASSIGNED_SHIPMENTS_SUBCOLLECTION),
        where('confirmed', '==', false)
      );

      const snapshot = await getDocs(q);
      setCount(snapshot.size);
    } catch (err: any) {
      console.error('[useAssignedShipmentsCount] Error fetching count:', err);
      setError(err.message || 'Failed to fetch assigned shipments count');
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [user, userRole]);

  /**
   * Refresh count
   */
  const refresh = useCallback(async () => {
    await fetchCount();
  }, [fetchCount]);

  // Initial fetch
  useEffect(() => {
    if (userRole === 'sales-representative') {
      fetchCount();
    } else {
      setLoading(false);
      setCount(0);
    }
  }, [fetchCount, userRole]);

  // Real-time subscription for sales reps
  useEffect(() => {
    if (!user || userRole !== 'sales-representative') {
      return;
    }

    const q = query(
      collection(db, USERS_COLLECTION, user.id, ASSIGNED_SHIPMENTS_SUBCOLLECTION),
      where('confirmed', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setCount(snapshot.size);
    }, (err) => {
      console.error('[useAssignedShipmentsCount] Subscription error:', err);
      setError(err.message || 'Failed to subscribe to assigned shipments');
    });

    return () => {
      unsubscribe();
    };
  }, [user, userRole]);

  return {
    count,
    loading,
    error,
    refresh
  };
};

